import React, { useEffect, useState, useRef } from 'react';
import { SunIcon, MoonIcon, BellIcon, UserIcon, XIcon, CheckIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { getRoleName } from '../../utils/helpers';
export function Header() {
  const {
    user
  } = useAuth();
  const {
    theme,
    toggleTheme
  } = useTheme();
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotification
  } = useNotifications();
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <header className="h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-6 fixed top-0 right-0 left-64 z-10" data-id="element-24">
      {/* Left side */}
      <div data-id="element-25">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-26">
          Bienvenido, {user?.name}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-27">
          {user?.role && getRoleName(user.role)}
        </p>
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-4" data-id="element-28">
        {/* Theme toggle */}
        <button onClick={toggleTheme} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" aria-label={theme === 'light' ? 'Activar modo oscuro' : 'Activar modo claro'} data-id="element-29">
          {theme === 'light' ? <MoonIcon className="w-5 h-5" data-id="element-30" /> : <SunIcon className="w-5 h-5" data-id="element-31" />}
        </button>

        {/* Notifications */}
        <div className="relative" ref={notificationRef} data-id="element-32">
          <button onClick={() => setShowNotifications(!showNotifications)} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors relative" aria-label="Notificaciones" data-id="element-33">
            <BellIcon className="w-5 h-5" data-id="element-34" />
            {unreadCount > 0 && <span className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs text-white font-bold" data-id="element-35">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>}
          </button>

          {showNotifications && <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50" data-id="element-36">
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700" data-id="element-37">
                <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-38">
                  Notificaciones {unreadCount > 0 && `(${unreadCount})`}
                </h3>
                {notifications.length > 0 && <button onClick={markAllAsRead} className="text-sm text-sky-600 dark:text-sky-400 hover:underline" data-id="element-39">
                    Marcar todas como leídas
                  </button>}
              </div>
              <div className="max-h-96 overflow-y-auto" data-id="element-40">
                {notifications.length === 0 ? <div className="p-8 text-center text-gray-500 dark:text-gray-400" data-id="element-41">
                    <BellIcon className="w-12 h-12 mx-auto mb-2 opacity-50" data-id="element-42" />
                    <p data-id="element-43">No hay notificaciones</p>
                  </div> : notifications.map(notification => <div key={notification.id} className={`p-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${!notification.read ? 'bg-sky-50 dark:bg-sky-900/10' : ''}`} data-id="element-44">
                      <div className="flex items-start gap-3" data-id="element-45">
                        <span className="text-2xl" data-id="element-46">
                          {notification.type === 'appointment_created' ? '📅' : notification.type === 'appointment_cancelled' ? '❌' : notification.type === 'appointment_rescheduled' ? '🔄' : notification.type === 'appointment_reminder' ? '⏰' : '🔔'}
                        </span>
                        <div className="flex-1 min-w-0" data-id="element-47">
                          <div className="flex items-start justify-between gap-2" data-id="element-48">
                            <h4 className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-49">
                              {notification.title}
                            </h4>
                            <button onClick={() => clearNotification(notification.id)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-50">
                              <XIcon className="w-4 h-4" data-id="element-51" />
                            </button>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" data-id="element-52">
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-3 mt-2" data-id="element-53">
                            <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-54">
                              {(() => {
                        const diff = new Date().getTime() - new Date(notification.createdAt).getTime();
                        const minutes = Math.floor(diff / 60000);
                        const hours = Math.floor(minutes / 60);
                        const days = Math.floor(hours / 24);
                        if (minutes < 1) return 'Ahora';
                        if (minutes < 60) return `Hace ${minutes} min`;
                        if (hours < 24) return `Hace ${hours}h`;
                        return `Hace ${days}d`;
                      })()}
                            </span>
                            {!notification.read && <button onClick={() => markAsRead(notification.id)} className="text-xs text-sky-600 dark:text-sky-400 hover:underline flex items-center gap-1" data-id="element-55">
                                <CheckIcon className="w-3 h-3" data-id="element-56" /> Marcar como
                                leída
                              </button>}
                          </div>
                        </div>
                      </div>
                    </div>)}
              </div>
            </div>}
        </div>

        {/* User info (no dropdown, just display) */}
        <div className="flex items-center gap-3 pl-4 border-l border-gray-200 dark:border-gray-700" data-id="element-57">
          <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 bg-sky-100 dark:bg-sky-900 flex items-center justify-center ring-2 ring-white dark:ring-gray-800 shadow-sm" data-id="element-58">
            {user?.avatar ? <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" data-id="element-59" /> : <UserIcon className="w-6 h-6 text-sky-600 dark:text-sky-400" data-id="element-60" />}
          </div>
          <div className="hidden sm:block" data-id="element-61">
            <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-62">
              {user?.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-63">
              {user?.email}
            </p>
          </div>
        </div>
      </div>
    </header>;
}